import {
  selAnnotations,
  selAnnotationsPluginState,
  type AnnotationBase
} from '@st/document-viewer-kit/plugins/annotations'
import type {
  Comment,
  CommentAnnotation,
  ContractField,
  DocumentAnnotation,
  Rect,
  STDocument
} from '@st/sdk'
import { isNotEmpty } from '@st/util/json-value'
import { match } from '@st/pattern'
import { schemaPathLabel, type JSONSchema } from './schema-document-data'

type FieldGeometry = {
  id: string
  time: string
  type: 'field'
  pageNumber: number
  bounds: Rect
}

export type ContractFieldAnnotation = FieldGeometry & {
  field: ContractField
} & (
    | { mode: 'editing' | 'viewing'; signerRole: string }
    | { mode: 'signing'; label: string; value: string; muted: boolean; canSign: boolean }
  )

export type ViewerFieldAnnotation =
  | (FieldGeometry & { mode: 'data'; key?: string; label: string })
  | ContractFieldAnnotation

export type ViewerCommentAnnotation = Omit<Comment, 'documentId'> & { type: 'comment' }

export function isCommentAnnotation(
  annotation: AnnotationBase
): annotation is ViewerCommentAnnotation {
  return annotation.type == 'comment'
}

export type STViewerAnnotation =
  | DocumentAnnotation
  | ViewerCommentAnnotation
  | ViewerFieldAnnotation
type RightSidebarMode = 'comments' | 'fields' | 'none'

type ViewerAnnotationsState = {
  pluginStates: Record<string, unknown>
}

export function resolveViewerCommentAnnotations({
  comments,
  documentId,
  editable,
  pageNumberByAnchor
}: {
  comments: Comment[]
  documentId: string
  editable: boolean
  pageNumberByAnchor?: Record<string, number>
}): Record<string, ViewerCommentAnnotation> {
  // The domain has one Comment model. This application boundary supplies the
  // DVK facts needed for interaction: fixed comments already have geometry;
  // anchored comments get a page from the current Formation layout and receive
  // measured bounds later when that page renders. Source does not create a
  // second annotation species, and readOnly never removes selection.
  const annotations: Record<string, ViewerCommentAnnotation> = {}

  for (const comment of comments) {
    if (comment.documentId != documentId) continue

    const readOnly = !editable || comment.readOnly

    if (comment.anchor != undefined) {
      const pageNumber = pageNumberByAnchor?.[comment.anchor]
      if (pageNumber == undefined) continue

      annotations[comment.id] = {
        ...comment,
        type: 'comment',
        readOnly,
        pageNumber
      }
      continue
    }

    if (comment.pageNumber == undefined || comment.bounds == undefined) {
      throw new Error(`Bounded comment ${comment.id} has no page bounds`)
    }

    annotations[comment.id] = { ...comment, type: 'comment', readOnly }
  }

  return annotations
}

export function toBoundedCommentAnnotation(
  comment: Omit<ViewerCommentAnnotation, 'readOnly'> | CommentAnnotation
): CommentAnnotation {
  if (comment.pageNumber == undefined || !comment.bounds) {
    throw new Error(`Comment ${comment.id} has not been positioned`)
  }

  return {
    id: comment.id,
    time: comment.time,
    type: 'comment',
    authorId: comment.authorId,
    pageNumber: comment.pageNumber,
    content: comment.content,
    bounds: comment.bounds
  }
}

export function resolveDocumentDataFieldAnnotations(
  doc: STDocument,
  dataSchema?: JSONSchema
): Record<string, ViewerFieldAnnotation> {
  const annotations: Record<string, ViewerFieldAnnotation> = {}
  const occurrences: Record<string, number> = {}

  for (const field of doc.fields) {
    if (field.bounds == undefined) {
      continue
    }

    const annotationId = fieldAnnotationId(field.path, field.pageNumber, occurrences)

    annotations[annotationId] = viewerFieldAnnotation({
      id: annotationId,
      mode: 'data',
      key: field.path,
      pageNumber: field.pageNumber,
      bounds: field.bounds,
      label: schemaPathLabel(dataSchema, field.path)
    })
  }

  return annotations
}

export function viewerFieldAnnotation<
  const T extends { id: string; pageNumber: number; bounds: Rect }
>(annotation: T): T & { time: string; type: 'field' } {
  return {
    ...annotation,
    time: 'synthetic',
    type: 'field'
  }
}

export function selViewerAnnotationsByPageNumber(state: ViewerAnnotationsState) {
  const grouped: Record<number, STViewerAnnotation[]> = {}

  for (const annotation of Object.values(selAnnotations<STViewerAnnotation>(state))) {
    if (annotation.pageNumber == undefined) continue
    const pageAnnotations = grouped[annotation.pageNumber] ?? []
    pageAnnotations.push(annotation)
    grouped[annotation.pageNumber] = pageAnnotations
  }

  return grouped
}

export function selRightSidebarMode(state: ViewerAnnotationsState): RightSidebarMode {
  return match({
    selectedToolName: selAnnotationsPluginState(state).selectedToolName,
    hasComments: isNotEmpty(selAnnotations(state, isCommentAnnotation)),
    hasFields: isNotEmpty(selAnnotations(state, isViewerFieldAnnotation))
  })
    .returnType<RightSidebarMode>()
    .with({ selectedToolName: 'field', hasFields: true }, () => 'fields')
    .with({ hasComments: true }, () => 'comments')
    .otherwise(() => 'none')
}

function fieldAnnotationId(key: string, pageNumber: number, occurrences: Record<string, number>) {
  const occurrenceKey = `${key}:${pageNumber}`
  const occurrenceIndex = (occurrences[occurrenceKey] ?? 0) + 1
  occurrences[occurrenceKey] = occurrenceIndex

  return `field:${key}:${pageNumber}:${occurrenceIndex}`
}

function isViewerFieldAnnotation(annotation: AnnotationBase): annotation is ViewerFieldAnnotation {
  return annotation.type == 'field'
}
